import { overview } from '../api/developer.js';
import { getDeveloper, isDeveloperSignedIn } from '../api/developerSession.js';
import { currentParams } from '../../shared/state/urlState.js';
import { ACCOUNT_PARAM } from '../accounts.js';

/**
 * Which accounts this person is part of, and which one the application is about right now.
 *
 * The customer application's sidebar is every team this person is part of, with its pages underneath;
 * the guard that keeps a personal team to its three pages reads the same list. It is the same request
 * the profile page makes - `GET /api/v1/seats/me/overview`, which is only ever about the caller - so it
 * is loaded here once per navigation and shared, rather than asked for by every element that shows it.
 *
 * The list belongs to the session it was loaded for. A switch to another account and a borrowed view both
 * replace the session while a page is open, and the request that was in flight for the old one would
 * otherwise fill the sidebar with somebody else's teams. So the list and the request in flight carry the
 * token they were asked with, and a load for another session starts afresh and ignores what the old one
 * still answers.
 *
 * Which account is current is decided in this order: the one the address names, then the one chosen
 * last time on this browser, then the first one owned, then the first one at all. The address always
 * wins, because a shared link must open on the account it names whatever this browser remembers.
 */

const STORAGE_KEY = 'covdbg.account';

let accounts = [];
let loaded = false;
let inflight = null;

// The session the list and the request in flight are about.
let owner = null;

const listeners = new Set();

function notify()
{
   for (const listener of listeners)
   {
      listener(accounts);
   }
}

/** Every account this person owns or holds a seat in, as the overview lists them. Empty until loaded. */
export function getAccounts()
{
   return accounts;
}

export function isLoaded()
{
   return loaded;
}

/**
 * Loads the list, once at a time.
 *
 * Every navigation asks again, because ownership and seats change under the reader - an invitation
 * accepted, a team handed over - and a sidebar that showed yesterday's list would be a sidebar that
 * had to be reloaded to be believed. One request per page is what the profile page costs anyway.
 */
export function loadAccounts()
{
   if (!isDeveloperSignedIn())
   {
      clearAccounts();

      return Promise.resolve(accounts);
   }

   const token = getDeveloper()?.token ?? null;

   if (token !== owner)
   {
      // Another session than the list was loaded for: what it holds and what is still in flight are
      // about somebody else, and showing them until the new answer arrives would be showing the wrong
      // person's teams.
      clearAccounts();
      owner = token;
   }

   if (!inflight)
   {
      const request = overview()
         .then(data =>
         {
            if (owner === token)
            {
               accounts = data?.accounts ?? [];
               loaded = true;
               notify();
            }

            return accounts;
         })
         .catch(() =>
         {
            // A failed load keeps whatever was shown: the page itself reports the failure, and a sidebar
            // that vanished as well would leave nowhere to go.
            return accounts;
         })
         .finally(() =>
         {
            if (inflight === request)
            {
               inflight = null;
            }
         });

      inflight = request;
   }

   return inflight;
}

/** Forgets the list, for a sign-out or a change of session. What is still in flight is dropped with it. */
export function clearAccounts()
{
   accounts = [];
   loaded = false;
   inflight = null;
   owner = null;
   notify();
}

export function onAccountsChange(listener)
{
   listeners.add(listener);

   return () => listeners.delete(listener);
}

/** The account the address names, if it names one. */
export function addressedAccountId()
{
   return currentParams().get(ACCOUNT_PARAM) ?? '';
}

/**
 * The account the application is about: the address, then this browser's last choice, then the best
 * default. Null with no accounts at all.
 */
export function currentAccount()
{
   const addressed = addressedAccountId();

   if (addressed)
   {
      const named = accounts.find(entry => entry.id === addressed);

      // An address naming an account this person is not part of is still that account: the page it
      // opens answers for itself, and the sidebar then offers the way back.
      return named ?? { id: addressed, name: null, owner: false, unknown: true };
   }

   const remembered = read();
   const kept = remembered ? accounts.find(entry => entry.id === remembered) : null;

   return kept ?? accounts.find(entry => entry.owner) ?? accounts[0] ?? null;
}

/** Remembers the account a page was about, so the next visit opens on it. */
export function rememberAccount(accountId)
{
   try
   {
      if (accountId)
      {
         localStorage.setItem(STORAGE_KEY, accountId);
      }
   }
   catch
   {
      // Storage refused - a private window, a policy. Remembering is a convenience and nothing depends
      // on it.
   }
}

function read()
{
   try
   {
      return localStorage.getItem(STORAGE_KEY) ?? '';
   }
   catch
   {
      return '';
   }
}
